// 1-bit dot bitmap -> needle-printer style grayscale image.
//
// An MPS-803 prints 60 dots per inch across and 72 rows per inch down. We draw
// at 360 dpi in both directions, so one dot cell is 6 x 5 pixels and pixels
// stay square. Each fired pin leaves a round, soft-edged ink dot; where dots
// touch or overlap the ink merges (screen blend, never darker than full ink).
//
// Output: { width, height, ptPerPxX, ptPerPxY, gray } where gray is one byte
// per pixel, 255 = bare paper, 0 = full ink.

export const CELL_X = 6;
export const CELL_Y = 5;
// Dot radius in pixels (1 px = 1/360 in = 0.0706 mm). 3.0 px = 0.21 mm, a bit
// over half the dot pitch (0.42 x 0.35 mm): neighbours merge into solid
// strokes but single dots stay visible, like a ribbon print. (2.6 looked thin,
// 3.3 blobby.)
export const DEFAULT_RADIUS = 3.0;

export function dotKernel(radius = DEFAULT_RADIUS) {
  // The dot centre sits in the middle of its cell, so the sub-pixel position
  // is the same for every dot and one kernel serves all of them.
  const cx = CELL_X / 2;
  const cy = CELL_Y / 2;
  const x0 = Math.floor(cx - radius - 1);
  const y0 = Math.floor(cy - radius - 1);
  const x1 = Math.ceil(cx + radius + 1);
  const y1 = Math.ceil(cy + radius + 1);
  const w = x1 - x0;
  const h = y1 - y0;
  const ink = new Uint8Array(w * h);
  for (let j = 0; j < h; j++) {
    for (let i = 0; i < w; i++) {
      const dx = x0 + i + 0.5 - cx;
      const dy = y0 + j + 0.5 - cy;
      const cover = Math.min(1, Math.max(0, radius - Math.hypot(dx, dy) + 0.5));
      ink[j * w + i] = Math.round(cover * 255);
    }
  }
  return { x0, y0, w, h, ink };
}

export function needleRender(bm, radius = DEFAULT_RADIUS) {
  const width = bm.width * CELL_X;
  const height = bm.height * CELL_Y;
  const gray = new Uint8Array(width * height).fill(255);
  const k = dotKernel(radius);
  for (let y = 0; y < bm.height; y++) {
    for (let x = 0; x < bm.width; x++) {
      if (!bm.data[y * bm.width + x]) continue;
      const px = x * CELL_X + k.x0;
      const py = y * CELL_Y + k.y0;
      for (let j = 0; j < k.h; j++) {
        const gy = py + j;
        if (gy < 0 || gy >= height) continue;
        for (let i = 0; i < k.w; i++) {
          const gx = px + i;
          if (gx < 0 || gx >= width) continue;
          const c = k.ink[j * k.w + i];
          if (c === 0) continue;
          // paper' = paper * (1 - c): ink coverage merges like a screen blend
          const idx = gy * width + gx;
          gray[idx] = Math.round((gray[idx] * (255 - c)) / 255);
        }
      }
    }
  }
  return { width, height, ptPerPxX: 1.2 / CELL_X, ptPerPxY: 1 / CELL_Y, gray };
}

// gray -> RGBA for canvas / ImageData.
export function grayToRGBA(img) {
  const out = new Uint8ClampedArray(img.width * img.height * 4);
  for (let i = 0; i < img.gray.length; i++) {
    const v = img.gray[i];
    out[i * 4] = out[i * 4 + 1] = out[i * 4 + 2] = v;
    out[i * 4 + 3] = 255;
  }
  return out;
}
